import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { useShadowRoot } from "../../context/VNInvestChartsContext";
import styles from "../../styles/drawing-dock.module.css";
export default function DrawingDock({ isOpen, activeTab, labels, position, initialDragDelta, initialSizeDelta, onLayoutChange, onClose, onTabChange, headerActions, propertiesContent, objectsContent, templatesContent, }) {
    const shadowRoot = useShadowRoot();
    const [dragDelta, setDragDelta] = useState(() => initialDragDelta ?? { x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [sizeDelta, setSizeDelta] = useState(() => initialSizeDelta ?? { w: 0, h: 0 });
    const [isResizing, setIsResizing] = useState(false);
    const rootRef = useRef(null);
    const dragStartRef = useRef(null);
    const resizeStartRef = useRef(null);
    const dragDeltaRef = useRef(dragDelta);
    dragDeltaRef.current = dragDelta;
    const sizeDeltaRef = useRef(sizeDelta);
    sizeDeltaRef.current = sizeDelta;
    const onLayoutChangeRef = useRef(onLayoutChange);
    onLayoutChangeRef.current = onLayoutChange;
    useEffect(() => {
        if (!isDragging)
            return;
        const handlePointerMove = (e) => {
            const pe = e;
            if (!dragStartRef.current)
                return;
            if (pe.pointerId !== dragStartRef.current.pointerId)
                return;
            const nextX = dragStartRef.current.baseX + (pe.clientX - dragStartRef.current.startX);
            const nextY = dragStartRef.current.baseY + (pe.clientY - dragStartRef.current.startY);
            const baseLeft = position?.x ?? 16;
            const baseTop = position?.y ?? 16;
            const node = rootRef.current;
            const width = node?.offsetWidth ?? 360;
            const height = node?.offsetHeight ?? 420;
            const maxOffsetX = Math.max(0, window.innerWidth - width - 8) - baseLeft;
            const maxOffsetY = Math.max(0, window.innerHeight - height - 8) - baseTop;
            const minOffsetX = 8 - baseLeft;
            const minOffsetY = 8 - baseTop;
            setDragDelta({
                x: Math.min(maxOffsetX, Math.max(minOffsetX, nextX)),
                y: Math.min(maxOffsetY, Math.max(minOffsetY, nextY)),
            });
        };
        const handlePointerUp = (e) => {
            const pe = e;
            if (!dragStartRef.current)
                return;
            if (pe.pointerId !== dragStartRef.current.pointerId)
                return;
            dragStartRef.current = null;
            setIsDragging(false);
            onLayoutChangeRef.current?.({ dragDelta: dragDeltaRef.current, sizeDelta: sizeDeltaRef.current });
        };
        // Use shadowRoot for capture-phase listeners in Shadow DOM mode
        const target = shadowRoot ?? document;
        target.addEventListener("pointermove", handlePointerMove, { capture: true });
        target.addEventListener("pointerup", handlePointerUp, { capture: true });
        target.addEventListener("pointercancel", handlePointerUp, { capture: true });
        return () => {
            target.removeEventListener("pointermove", handlePointerMove, { capture: true });
            target.removeEventListener("pointerup", handlePointerUp, { capture: true });
            target.removeEventListener("pointercancel", handlePointerUp, { capture: true });
        };
    }, [isDragging, position?.x, position?.y, shadowRoot]);
    useEffect(() => {
        if (!isResizing)
            return;
        const handlePointerMove = (e) => {
            const pe = e;
            if (!resizeStartRef.current)
                return;
            if (pe.pointerId !== resizeStartRef.current.pointerId)
                return;
            const dw = pe.clientX - resizeStartRef.current.startX;
            const dh = pe.clientY - resizeStartRef.current.startY;
            setSizeDelta({
                w: Math.min(Math.max(resizeStartRef.current.baseW + dw, 280 - 360), 600 - 360),
                h: Math.min(Math.max(resizeStartRef.current.baseH + dh, 200 - 200), 800 - 200),
            });
        };
        const handlePointerUp = (e) => {
            const pe = e;
            if (!resizeStartRef.current)
                return;
            if (pe.pointerId !== resizeStartRef.current.pointerId)
                return;
            resizeStartRef.current = null;
            setIsResizing(false);
            onLayoutChangeRef.current?.({ dragDelta: dragDeltaRef.current, sizeDelta: sizeDeltaRef.current });
        };
        const target = shadowRoot ?? document;
        target.addEventListener("pointermove", handlePointerMove, { capture: true });
        target.addEventListener("pointerup", handlePointerUp, { capture: true });
        target.addEventListener("pointercancel", handlePointerUp, { capture: true });
        return () => {
            target.removeEventListener("pointermove", handlePointerMove, { capture: true });
            target.removeEventListener("pointerup", handlePointerUp, { capture: true });
            target.removeEventListener("pointercancel", handlePointerUp, { capture: true });
        };
    }, [isResizing, shadowRoot]);
    // R3: clamp a restored drag offset back into the viewport on open, so a dock
    // persisted on a wide screen is not stranded off-screen on a narrow one.
    useEffect(() => {
        if (!isOpen)
            return;
        const node = rootRef.current;
        if (!node)
            return;
        const baseLeft = position?.x ?? 16;
        const baseTop = position?.y ?? 16;
        const width = node.offsetWidth || 360;
        const height = node.offsetHeight || 420;
        const maxOffsetX = Math.max(0, window.innerWidth - width - 8) - baseLeft;
        const maxOffsetY = Math.max(0, window.innerHeight - height - 8) - baseTop;
        const minOffsetX = 8 - baseLeft;
        const minOffsetY = 8 - baseTop;
        setDragDelta((current) => {
            const clampedX = Math.min(maxOffsetX, Math.max(minOffsetX, current.x));
            const clampedY = Math.min(maxOffsetY, Math.max(minOffsetY, current.y));
            if (clampedX === current.x && clampedY === current.y)
                return current;
            const next = { x: clampedX, y: clampedY };
            onLayoutChangeRef.current?.({ dragDelta: next, sizeDelta: sizeDeltaRef.current });
            return next;
        });
        // Run once per open; position base changes are the only relevant re-trigger.
    }, [isOpen, position?.x, position?.y]);
    if (!isOpen) {
        return null;
    }
    const left = (position?.x ?? 16) + dragDelta.x;
    const top = (position?.y ?? 16) + dragDelta.y;
    const width = 360 + sizeDelta.w;
    const height = 200 + sizeDelta.h;
    const handleHeaderPointerDown = (e) => {
        // Ignore clicks on buttons inside the header
        if (e.target.closest("button"))
            return;
        if (e.button !== 0)
            return;
        e.preventDefault();
        dragStartRef.current = {
            pointerId: e.pointerId,
            startX: e.clientX,
            startY: e.clientY,
            baseX: dragDelta.x,
            baseY: dragDelta.y,
        };
        setIsDragging(true);
    };
    const resolvedContent = activeTab === "properties"
        ? (propertiesContent ?? _jsx("div", { className: styles.empty, children: labels.emptyProperties }))
        : activeTab === "objects"
            ? (objectsContent ?? _jsx("div", { className: styles.empty, children: labels.emptyObjects }))
            : (templatesContent ?? _jsx("div", { className: styles.empty, children: labels.emptyTemplates }));
    return (_jsxs("section", { ref: rootRef, className: styles.dock, style: { left, top, width, minHeight: height, maxHeight: height }, "aria-label": labels.title, children: [_jsxs("header", { className: styles.header, style: { cursor: isDragging ? "grabbing" : "grab" }, onPointerDown: handleHeaderPointerDown, children: [_jsxs("div", { className: styles.titleBlock, children: [_jsx("strong", { children: labels.title }), _jsx("span", { children: activeTab === "properties" ? labels.properties : activeTab === "objects" ? labels.objects : labels.templates })] }), _jsxs("div", { className: styles.headerActions, children: [headerActions, _jsx("button", { type: "button", className: styles.icon, onClick: onClose, "aria-label": labels.close, children: "\u00D7" })] })] }), _jsx("nav", { className: styles.tabs, "aria-label": labels.title, role: "tablist", children: [
                    ["properties", labels.properties],
                    ["objects", labels.objects],
                    ["templates", labels.templates],
                ].map(([tab, label]) => (_jsx("button", { type: "button", role: "tab", "aria-selected": activeTab === tab, className: `${styles.tab} ${activeTab === tab ? styles.tabActive : ""}`, onClick: () => onTabChange(tab), children: label }, tab))) }), _jsx("div", { className: styles.body, children: resolvedContent }), _jsx("div", { className: styles.resizeHandle, onPointerDown: (e) => {
                    if (e.button !== 0)
                        return;
                    e.preventDefault();
                    e.stopPropagation();
                    resizeStartRef.current = {
                        pointerId: e.pointerId,
                        startX: e.clientX,
                        startY: e.clientY,
                        baseW: sizeDelta.w,
                        baseH: sizeDelta.h,
                    };
                    setIsResizing(true);
                }, "aria-label": labels.resize })] }));
}
